/**
 * GoalsAudit.tsx — manager/exec "Goals Audit" tab on the Crew board.
 *
 * Audits goal attainment (goal vs actual vs %) over flexible time ranges:
 * This week · This month · Last 30 · Last 90 · Last 6 months · This year.
 * Slice by Company total / By role / By person.
 *
 * Goals: weekly targets in `crew_goals` are SUMMED across every week-Monday
 * inside the selected range (per Greg, May 2026). Actuals come from
 * `crew_daily_points`. Both via crewQueries helpers — no new DB objects.
 *
 * Data reality (Jun 2026): weekly goals only exist from 2026-04-27, and only
 * for apps/claims/builds_scheduled/builds. Ranges with no goal weeks render
 * actual-only with a coverage note. Revenue has no goal — shown actual-only.
 * Year-over-year month-by-month is intentionally NOT here yet (apps/claims/IA
 * have no pre-2026 history to compare against).
 *
 * Gated to TL / ASM / executives + super-users via isCrewManager(). Non-managers
 * never see the tab (Crew.tsx hides it) but we guard here too.
 */
import { useEffect, useMemo, useState } from "react";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { useAuth } from "../../context/AuthContext";
import { isCrewManager } from "./crewAuth";
import GiveSpiffModal from "./GiveSpiffModal";
import {
  fetchLeaderboard,
  fetchAuditActuals,
  fetchAuditGoals,
  fetchAuditSpiffs,
  fetchAuditSpiffDetail,
  AUDIT_RANGE_ORDER,
  AUDIT_RANGE_LABELS,
  type AuditRange,
  type AuditGoals,
  type AuditSpiffLine,
  type CrewLeaderboardRow,
  type WallStat,
  type GoalMetricId,
} from "./crewQueries";

// ───────────────────── role grouping (mirrors LeaderboardWall) ─────────────────────
type RoleKey = "asm" | "team_lead" | "restore_manager";
const ROLE_GROUPS: Array<{ id: RoleKey; label: string; short: string }> = [
  { id: "asm", label: "Area Sales Managers", short: "ASM" },
  { id: "team_lead", label: "Team Leads", short: "TL" },
  { id: "restore_manager", label: "Restore Managers", short: "RM" },
];

// ───────────────────── metric meta ─────────────────────
const METRICS: Array<{ id: GoalMetricId; label: string; color: string }> = [
  { id: "apps", label: "Apps", color: "#3a7ba6" },
  { id: "claims", label: "Claims", color: "#5a4a7a" },
  { id: "builds_scheduled", label: "Signed IA", color: "#1e5a7a" },
  { id: "builds", label: "Builds", color: "#2a7a43" },
];

// ───────────────────── helpers ─────────────────────
function fmtCount(n: number): string {
  return Number.isInteger(n) ? n.toString() : n.toFixed(1).replace(/\.0$/, "");
}
function fmtMoney(cents: number): string {
  const dollars = cents / 100;
  if (dollars === 0) return "—";
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  if (dollars >= 10_000) return `$${Math.round(dollars / 1000)}k`;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `$${Math.round(dollars)}`;
}
function initialsFrom(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
const AVATAR_BG = ["#2a2e30", "#345c72", "#c14a1b", "#1e5a7a", "#3a7ba6", "#2a7a43", "#5a4a7a", "#7a3a5a"];
function avatarColor(userId: string) {
  let h = 0;
  for (const c of userId) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_BG[h % AVATAR_BG.length]!;
}
function fmtShort(d: Date) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
function fmtRange(start: Date, end: Date) {
  const inclusiveEnd = new Date(end);
  inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
  return `${fmtShort(start)} → ${fmtShort(inclusiveEnd)}`;
}
function fmtLong(iso: string) {
  return new Date(iso + "T12:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Attainment %, or null when there's no goal to measure against. */
function attainment(actual: number, goal: number): number | null {
  if (!goal || goal <= 0) return null;
  return (actual / goal) * 100;
}
function attainColor(pct: number | null): string {
  if (pct === null) return "var(--br-peach, #ff9e7a)";
  if (pct >= 100) return "var(--success, #2a7a43)";
  if (pct >= 75) return "var(--br-orange, #f46530)";
  return "#b3261e";
}

// ───────────────────── derived row shape ─────────────────────
interface AuditRow {
  user_id: string;
  full_name: string;
  role: RoleKey;
  actual: Record<GoalMetricId, number>;
  revenue_cents: number;
  spiffs_cents: number;
  goal: Partial<Record<GoalMetricId, number>>;
  on_leave: boolean;
}

// ───────────────────── component ─────────────────────
type Breakdown = "company" | "role" | "person";

export default function GoalsAudit() {
  const { member } = useAuth();
  const canUse = isCrewManager(member);

  const [range, setRange] = useState<AuditRange>("month");
  const [breakdown, setBreakdown] = useState<Breakdown>("company");
  const [rows, setRows] = useState<CrewLeaderboardRow[]>([]);
  const [stats, setStats] = useState<Record<string, WallStat>>({});
  const [spiffs, setSpiffs] = useState<Record<string, number>>({});
  const [spiffDetail, setSpiffDetail] = useState<AuditSpiffLine[]>([]);
  const [goals, setGoals] = useState<AuditGoals | null>(null);
  const [windowLabel, setWindowLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [spiffModalOpen, setSpiffModalOpen] = useState(false);

  async function reload(r: AuditRange) {
    try {
      setErr(null);
      setLoading(true);
      const [lb, act, g, sp, spd] = await Promise.all([
        fetchLeaderboard(),
        fetchAuditActuals(r),
        fetchAuditGoals(r),
        fetchAuditSpiffs(r),
        fetchAuditSpiffDetail(r),
      ]);
      setRows(lb);
      setStats(act.stats);
      setSpiffs(sp.byUser);
      setSpiffDetail(spd);
      setGoals(g);
      setWindowLabel(fmtRange(act.window.start, act.window.end));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load goals audit");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (canUse) reload(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, canUse]);

  useRealtimeTable("crew_activity_log", () => canUse && reload(range));
  useRealtimeTable("crew_goals", () => canUse && reload(range));
  useRealtimeTable("spiffs", () => canUse && reload(range));

  /** Merge leaderboard (names/roles) + actuals + personal goals. */
  const auditRows: AuditRow[] = useMemo(() => {
    if (!goals) return [];
    return rows
      .filter((r) => ROLE_GROUPS.some((g) => g.id === (r.role as RoleKey)))
      .map((r) => {
        const s = stats[r.user_id];
        return {
          user_id: r.user_id,
          full_name: r.full_name,
          role: r.role as RoleKey,
          actual: {
            apps: Number(s?.apps ?? 0),
            claims: Number(s?.claims ?? 0),
            builds_scheduled: Number(s?.builds_scheduled ?? 0),
            builds: Number(s?.builds ?? 0),
          },
          revenue_cents: Number(s?.revenue_cents ?? 0),
          spiffs_cents: Number(spiffs[r.user_id] ?? 0),
          goal: goals.personalByUser[r.user_id] ?? {},
          on_leave: !!r.on_leave,
        };
      })
      // On-leave reps drop out of the audit unless the range has their actuals or goals.
      .filter((a) => !a.on_leave
        || Object.values(a.actual).some((v) => v > 0)
        || a.revenue_cents > 0
        || Object.values(a.goal).some((v) => (v ?? 0) > 0));
  }, [rows, stats, spiffs, goals]);

  /** Company totals (actual + goal with company-scope fallback to summed personal). */
  const companyAgg = useMemo(() => {
    const actual: Record<GoalMetricId, number> = { apps: 0, claims: 0, builds_scheduled: 0, builds: 0 };
    let revenue = 0;
    let spiffs_cents = 0;
    const personalSum: Record<GoalMetricId, number> = { apps: 0, claims: 0, builds_scheduled: 0, builds: 0 };
    for (const r of auditRows) {
      for (const m of METRICS) {
        actual[m.id] += r.actual[m.id];
        personalSum[m.id] += r.goal[m.id] ?? 0;
      }
      revenue += r.revenue_cents;
      spiffs_cents += r.spiffs_cents;
    }
    const goal: Record<GoalMetricId, number> = { apps: 0, claims: 0, builds_scheduled: 0, builds: 0 };
    for (const m of METRICS) {
      const explicit = goals?.company[m.id];
      goal[m.id] = explicit && explicit > 0 ? explicit : personalSum[m.id];
    }
    return { actual, goal, revenue, spiffs_cents };
  }, [auditRows, goals]);

  /** Per-role aggregate rows. */
  const roleAgg = useMemo(() => {
    return ROLE_GROUPS.map((g) => {
      const members = auditRows.filter((r) => r.role === g.id);
      const actual: Record<GoalMetricId, number> = { apps: 0, claims: 0, builds_scheduled: 0, builds: 0 };
      const goal: Record<GoalMetricId, number> = { apps: 0, claims: 0, builds_scheduled: 0, builds: 0 };
      let revenue = 0;
      let spiffs_cents = 0;
      for (const r of members) {
        for (const m of METRICS) {
          actual[m.id] += r.actual[m.id];
          goal[m.id] += r.goal[m.id] ?? 0;
        }
        revenue += r.revenue_cents;
        spiffs_cents += r.spiffs_cents;
      }
      return { ...g, count: members.length, actual, goal, revenue, spiffs_cents };
    });
  }, [auditRows]);

  /** Per-person rows grouped by role, sorted by builds desc then name. */
  const personGroups = useMemo(() => {
    return ROLE_GROUPS.map((g) => ({
      ...g,
      rows: auditRows
        .filter((r) => r.role === g.id)
        .sort((a, b) => b.actual.builds - a.actual.builds || a.full_name.localeCompare(b.full_name)),
    }));
  }, [auditRows]);

  if (!canUse) {
    return (
      <div style={{ padding: 24, color: "#fff", fontFamily: "var(--font-sans)" }}>
        This view is limited to team leads, ASMs, and leadership.
      </div>
    );
  }

  return (
    <div style={{ padding: "14px 14px 80px", background: "var(--br-slate)", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 12 }}>
        <div>
          <div
            style={{
              fontFamily: "var(--font-display, var(--font-sans))",
              color: "#fff",
              fontSize: 20,
              fontWeight: 900,
              letterSpacing: ".01em",
            }}
          >
            Goals Audit
          </div>
          <div style={{ color: "rgba(255,255,255,.6)", fontSize: 12, fontFamily: "var(--font-sans)", marginTop: 2 }}>
            Goal vs. actual attainment {windowLabel && `· ${windowLabel}`}
          </div>
        </div>
        <button
          onClick={() => setSpiffModalOpen(true)}
          style={{
            flexShrink: 0,
            border: 0,
            cursor: "pointer",
            borderRadius: 99,
            padding: "9px 14px",
            background: "var(--br-orange, #f46530)",
            color: "#fff",
            fontFamily: "var(--font-sans)",
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: ".02em",
            whiteSpace: "nowrap",
          }}
        >
          🏆 Give SPIFF
        </button>
      </div>

      {/* Range pills */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {AUDIT_RANGE_ORDER.map((r) => {
          const active = r === range;
          return (
            <button
              key={r}
              onClick={() => setRange(r)}
              style={{
                border: 0,
                cursor: "pointer",
                borderRadius: 99,
                padding: "7px 12px",
                fontFamily: "var(--font-sans)",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: ".04em",
                textTransform: "uppercase",
                color: active ? "#fff" : "rgba(255,255,255,.7)",
                background: active ? "var(--br-orange)" : "rgba(255,255,255,.08)",
              }}
            >
              {AUDIT_RANGE_LABELS[r]}
            </button>
          );
        })}
      </div>

      {/* Breakdown toggle */}
      <div
        style={{
          display: "flex",
          gap: 2,
          padding: 3,
          background: "rgba(255,255,255,.08)",
          borderRadius: 10,
          marginBottom: 12,
        }}
      >
        {(
          [
            { id: "company", label: "Company" },
            { id: "role", label: "By role" },
            { id: "person", label: "By person" },
          ] as const
        ).map((b) => {
          const active = breakdown === b.id;
          return (
            <button
              key={b.id}
              onClick={() => setBreakdown(b.id)}
              style={{
                flex: 1,
                border: 0,
                cursor: "pointer",
                background: active ? "var(--br-orange)" : "transparent",
                color: "#fff",
                padding: "9px 8px",
                borderRadius: 8,
                fontFamily: "var(--font-sans)",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: ".06em",
                textTransform: "uppercase",
              }}
            >
              {b.label}
            </button>
          );
        })}
      </div>

      {/* Coverage note */}
      {goals && <CoverageNote goals={goals} />}

      {err && (
        <div style={{ color: "#ffb4a8", fontFamily: "var(--font-sans)", fontSize: 13, padding: "12px 0" }}>
          {err}
        </div>
      )}
      {loading && !err && (
        <div style={{ color: "rgba(255,255,255,.6)", fontFamily: "var(--font-sans)", fontSize: 13, padding: "12px 0" }}>
          Loading…
        </div>
      )}

      {!loading && !err && goals && (
        <>
          {breakdown === "company" && (
            <CompanyView
              actual={companyAgg.actual}
              goal={companyAgg.goal}
              revenue={companyAgg.revenue}
              spiffs={companyAgg.spiffs_cents}
              spiffDetail={spiffDetail}
            />
          )}
          {breakdown === "role" && (
            <AuditTable
              firstColHeader="Role"
              rows={roleAgg.map((g) => ({
                key: g.id,
                label: g.short,
                sub: `${g.count} rep${g.count === 1 ? "" : "s"}`,
                tint: "#345c72",
                actual: g.actual,
                goal: g.goal,
                revenue: g.revenue,
                spiffs: g.spiffs_cents,
              }))}
            />
          )}
          {breakdown === "person" &&
            personGroups.map((g) => (
              <div key={g.id} style={{ marginBottom: 16 }}>
                <div
                  style={{
                    color: "var(--br-peach, #ff9e7a)",
                    fontFamily: "var(--font-sans)",
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: ".12em",
                    textTransform: "uppercase",
                    margin: "4px 2px 8px",
                  }}
                >
                  {g.label} ({g.rows.length})
                </div>
                {g.rows.length === 0 ? (
                  <div style={{ color: "rgba(255,255,255,.4)", fontSize: 12, fontFamily: "var(--font-sans)", padding: "0 2px 8px" }}>
                    No reps in this group.
                  </div>
                ) : (
                  <AuditTable
                    firstColHeader="Rep"
                    rows={g.rows.map((r) => ({
                      key: r.user_id,
                      label: r.full_name,
                      tint: avatarColor(r.user_id),
                      avatar: initialsFrom(r.full_name),
                      actual: r.actual,
                      goal: r.goal,
                      revenue: r.revenue_cents,
                      spiffs: r.spiffs_cents,
                    }))}
                  />
                )}
              </div>
            ))}
        </>
      )}

      {spiffModalOpen && member && (
        <GiveSpiffModal
          awardedBy={member.id}
          onClose={() => setSpiffModalOpen(false)}
          onSaved={() => {
            setSpiffModalOpen(false);
            reload(range);
          }}
        />
      )}
    </div>
  );
}

// ───────────────────── coverage note ─────────────────────
function CoverageNote({ goals }: { goals: AuditGoals }) {
  let text: string;
  if (!goals.earliestGoalWeekEver) {
    text = "No weekly goals have been set yet — showing actuals only.";
  } else if (goals.goalWeeksFound === 0) {
    text = `No weekly goals fall in this range (goals began ${fmtLong(goals.earliestGoalWeekEver)}) — showing actuals only.`;
  } else if (goals.goalWeeksFound < goals.weeksInRange) {
    text = `Goals cover ${goals.goalWeeksFound} of ${goals.weeksInRange} weeks in this range (weekly goals began ${fmtLong(goals.earliestGoalWeekEver)}). Attainment % weighs full-range actuals against that partial goal — read it directionally.`;
  } else {
    text = `Goals cover all ${goals.weeksInRange} week${goals.weeksInRange === 1 ? "" : "s"} in this range. Targets are summed weekly goals; the current week may still be in progress.`;
  }
  return (
    <div
      style={{
        background: "rgba(255,158,122,.12)",
        border: "1px solid rgba(255,158,122,.3)",
        borderRadius: 8,
        padding: "8px 10px",
        color: "rgba(255,255,255,.78)",
        fontFamily: "var(--font-sans)",
        fontSize: 11.5,
        lineHeight: 1.45,
        marginBottom: 12,
      }}
    >
      {text}
    </div>
  );
}

// ───────────────────── company view (card grid) ─────────────────────
function CompanyView({
  actual,
  goal,
  revenue,
  spiffs,
  spiffDetail,
}: {
  actual: Record<GoalMetricId, number>;
  goal: Record<GoalMetricId, number>;
  revenue: number;
  spiffs: number;
  spiffDetail: AuditSpiffLine[];
}) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
      {METRICS.map((m) => {
        const a = actual[m.id];
        const g = goal[m.id];
        const pct = attainment(a, g);
        return (
          <div
            key={m.id}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: "12px 14px",
              boxShadow: "0 1px 3px rgba(0,0,0,.18)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontFamily: "var(--font-sans)",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: ".06em",
                textTransform: "uppercase",
                color: "#5b6166",
              }}
            >
              <span style={{ width: 9, height: 9, borderRadius: 3, background: m.color }} />
              {m.label}
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 8 }}>
              <span style={{ fontFamily: "var(--font-display, var(--font-sans))", fontSize: 26, fontWeight: 900, color: "#22272a" }}>
                {fmtCount(a)}
              </span>
              <span style={{ fontFamily: "var(--font-sans)", fontSize: 13, color: "#9aa1a6" }}>
                {pct === null ? "actual" : `/ ${fmtCount(g)}`}
              </span>
            </div>
            <AttainBar pct={pct} />
          </div>
        );
      })}
      {/* Revenue (actual only) */}
      <div
        style={{
          gridColumn: "1 / -1",
          background: "#22272a",
          borderRadius: 12,
          padding: "12px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            color: "var(--br-peach, #ff9e7a)",
          }}
        >
          Revenue (actual)
        </div>
        <div style={{ fontFamily: "var(--font-display, var(--font-sans))", fontSize: 22, fontWeight: 900, color: "#fff" }}>
          {fmtMoney(revenue)}
        </div>
      </div>
      {/* Spiffs won — expandable breakdown so the week can be reviewed rep-by-rep */}
      <SpiffsWonPanel spiffs={spiffs} detail={spiffDetail} />
    </div>
  );
}

// ───────────────────── spiffs won (expandable breakdown) ─────────────────────
function SpiffsWonPanel({ spiffs, detail }: { spiffs: number; detail: AuditSpiffLine[] }) {
  const [open, setOpen] = useState(false);

  // Group by rep, sorted by total desc.
  const byRep = useMemo(() => {
    const map = new Map<string, { name: string; total: number; lines: AuditSpiffLine[] }>();
    for (const s of detail) {
      const e = map.get(s.user_id) ?? { name: s.user_name, total: 0, lines: [] };
      e.total += s.amount_cents;
      e.lines.push(s);
      map.set(s.user_id, e);
    }
    return [...map.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
  }, [detail]);

  return (
    <div style={{ gridColumn: "1 / -1", background: "var(--br-orange, #f46530)", borderRadius: 12, overflow: "hidden" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          border: 0,
          background: "transparent",
          cursor: "pointer",
          padding: "12px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: ".06em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,.92)",
          }}
        >
          🏆 Spiffs won
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontFamily: "var(--font-display, var(--font-sans))", fontSize: 22, fontWeight: 900, color: "#fff" }}>
            {fmtMoney(spiffs)}
          </span>
          <span style={{ color: "rgba(255,255,255,.92)", fontSize: 16, fontWeight: 900, lineHeight: 1 }}>
            {open ? "▴" : "▾"}
          </span>
        </div>
      </button>

      {open && (
        <div style={{ background: "rgba(255,255,255,.97)", padding: detail.length ? "8px 8px 10px" : "14px" }}>
          {detail.length === 0 ? (
            <div style={{ fontFamily: "var(--font-sans)", fontSize: 12.5, color: "#5b6166", textAlign: "center" }}>
              No SPIFFs won in this range yet.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {byRep.map((rep) => (
                <div key={rep.name} style={{ background: "#fff", borderRadius: 9, border: "1px solid #eceef0", overflow: "hidden" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 11px",
                      background: "#faf7f5",
                      borderBottom: "1px solid #f0ecea",
                    }}
                  >
                    <span style={{ fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 800, color: "#22272a" }}>
                      {rep.name}
                    </span>
                    <span style={{ fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 900, color: "var(--br-orange, #f46530)" }}>
                      {fmtMoney(rep.total)}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {rep.lines.map((l) => (
                      <div
                        key={l.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                          padding: "6px 11px",
                          fontFamily: "var(--font-sans)",
                          fontSize: 12,
                        }}
                      >
                        <span style={{ color: "#3a4045", minWidth: 0, flex: 1 }}>
                          {l.name}
                          <span style={{ color: "#9aa1a6", marginLeft: 6 }}>{fmtSpiffDay(l.claimed_at)}</span>
                        </span>
                        <span style={{ color: "#22272a", fontWeight: 700, whiteSpace: "nowrap" }}>
                          {fmtMoney(l.amount_cents)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Short ET day label for a SPIFF's claimed_at timestamp. */
function fmtSpiffDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function AttainBar({ pct }: { pct: number | null }) {
  const color = attainColor(pct);
  const width = pct === null ? 0 : Math.max(2, Math.min(100, pct));
  return (
    <div style={{ marginTop: 9 }}>
      <div style={{ height: 6, borderRadius: 99, background: "#eceef0", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${width}%`, background: color, transition: "width .3s" }} />
      </div>
      <div style={{ marginTop: 5, fontFamily: "var(--font-sans)", fontSize: 11, fontWeight: 800, color }}>
        {pct === null ? "No goal set" : `${Math.round(pct)}% of goal`}
      </div>
    </div>
  );
}

// ───────────────────── audit table (role + person) ─────────────────────
interface TableRow {
  key: string;
  label: string;
  sub?: string;
  tint: string;
  avatar?: string;
  actual: Record<GoalMetricId, number>;
  goal: Partial<Record<GoalMetricId, number>>;
  revenue: number;
  spiffs: number;
}

function AuditTable({ firstColHeader, rows }: { firstColHeader: string; rows: TableRow[] }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,.18)",
        marginBottom: 4,
      }}
    >
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 560 }}>
          <thead>
            <tr style={{ background: "#f4f5f6" }}>
              <Th left>{firstColHeader}</Th>
              {METRICS.map((m) => (
                <Th key={m.id}>{m.label}</Th>
              ))}
              <Th>Revenue</Th>
              <Th>Spiffs</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} style={{ borderTop: "1px solid #eceef0" }}>
                <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        width: 26,
                        height: 26,
                        borderRadius: 99,
                        background: r.tint,
                        color: "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: "var(--font-sans)",
                        fontSize: 10,
                        fontWeight: 800,
                      }}
                    >
                      {r.avatar ?? r.label.slice(0, 2).toUpperCase()}
                    </span>
                    <div>
                      <div style={{ fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 700, color: "#22272a" }}>
                        {r.label}
                      </div>
                      {r.sub && (
                        <div style={{ fontFamily: "var(--font-sans)", fontSize: 10, color: "#9aa1a6" }}>{r.sub}</div>
                      )}
                    </div>
                  </div>
                </td>
                {METRICS.map((m) => (
                  <GoalCell key={m.id} actual={r.actual[m.id]} goal={r.goal[m.id] ?? 0} />
                ))}
                <td style={{ padding: "10px 12px", textAlign: "center" }}>
                  <span style={{ fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 700, color: "#22272a" }}>
                    {fmtMoney(r.revenue)}
                  </span>
                </td>
                <td style={{ padding: "10px 12px", textAlign: "center" }}>
                  <span
                    style={{
                      fontFamily: "var(--font-sans)",
                      fontSize: 13,
                      fontWeight: 800,
                      color: r.spiffs > 0 ? "var(--br-orange, #f46530)" : "#b4babf",
                    }}
                  >
                    {fmtMoney(r.spiffs)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children, left }: { children: React.ReactNode; left?: boolean }) {
  return (
    <th
      style={{
        padding: "9px 12px",
        textAlign: left ? "left" : "center",
        fontFamily: "var(--font-sans)",
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: ".06em",
        textTransform: "uppercase",
        color: "#7a8085",
      }}
    >
      {children}
    </th>
  );
}

function GoalCell({ actual, goal }: { actual: number; goal: number }) {
  const pct = attainment(actual, goal);
  const color = attainColor(pct);
  return (
    <td style={{ padding: "8px 12px", textAlign: "center", whiteSpace: "nowrap" }}>
      <div style={{ fontFamily: "var(--font-sans)", fontSize: 13, fontWeight: 800, color: "#22272a" }}>
        {fmtCount(actual)}
        <span style={{ color: "#b4babf", fontWeight: 600 }}>
          {pct === null ? "" : ` / ${fmtCount(goal)}`}
        </span>
      </div>
      <div style={{ fontFamily: "var(--font-sans)", fontSize: 10, fontWeight: 800, color, marginTop: 2 }}>
        {pct === null ? "no goal" : `${Math.round(pct)}%`}
      </div>
    </td>
  );
}
